/**
 * Viewport, overlay and notification plumbing for the storefront widget.
 *
 * Everything here exists because a WooCommerce product page is hostile ground:
 * the theme may put `transform` on an ancestor (which re-roots every
 * `position: fixed` descendant), `overflow: hidden` on the summary column
 * (which silently kills `position: sticky`), a fixed header that eats the top
 * of the viewport, a sticky add-to-cart bar that eats the bottom, and the page
 * is viewed on phones whose visual viewport shrinks under the on-screen
 * keyboard and behind an in-app browser's own toolbars.
 *
 * The rules this module enforces:
 *   - every overlay (wizard, lightbox, map/song dialogs, face picker, toasts)
 *     lives in the browser's TOP LAYER via `<dialog>.showModal()` where
 *     available, so no ancestor transform, overflow or z-index can clip it;
 *     the fallback is `position: fixed` inside a body-level portal;
 *   - the page behind an open overlay is scroll-locked in a way that also
 *     holds on iOS Safari (html overflow:hidden alone does not), reference
 *     counted so stacked overlays release it exactly once;
 *   - the visual viewport (keyboard, URL bar, in-app toolbars) is tracked into
 *     CSS custom properties so overlays and toasts are sized from what the
 *     shopper can actually see, never from `100vh`;
 *   - notifications live in whichever layer is on top, because everything
 *     under an open modal dialog is inert and invisible to the shopper.
 *
 * The signatures here are a contract: main.ts, form.ts and facepick.ts code
 * against them, so they may be added to but not changed.
 */

export type ToastKind = 'error' | 'info' | 'success';

export interface LayerOptions {
  /** Where the layer element is appended: the widget's shadow root. */
  host: ShadowRoot | HTMLElement;
  /** Class on the layer element (e.g. `pz-overlay`, `pz-lightbox`). */
  className: string;
  /** The card/sheet inside the layer. */
  content: HTMLElement;
  /** Accessible name of the dialog. */
  label?: string;
  /** Close when the shopper taps the dimmed backdrop (default true). */
  closeOnBackdrop?: boolean;
  /** Close on Escape (default true). */
  closeOnEscape?: boolean;
  /** Called after the layer closed, however it closed. */
  onClose?: () => void;
  /** Called after the layer opened and is on screen. */
  onOpen?: () => void;
  /** Lock the page scroll while open (default true). */
  lockScroll?: boolean;
}

export interface LayerHandle {
  /** The layer element itself (a `<dialog>` where supported, else a `<div>`). */
  readonly element: HTMLElement;
  readonly isOpen: boolean;
  open(): void;
  close(): void;
  /** Removes the layer from the DOM for good. */
  destroy(): void;
}

/** True when `<dialog>.showModal()` is available (top layer, inert page). */
export function supportsDialog(): boolean {
  return (
    typeof HTMLDialogElement === 'function' && 'showModal' in HTMLDialogElement.prototype
  );
}

/**
 * The layers currently open, in the order they opened.
 *
 * The browser's own top layer already stacks them; this list exists so the
 * toast region can be re-parented into whichever one is on top — a notification
 * appended anywhere else is inert (and, under `showModal`, invisible) while a
 * dialog is open, which is exactly when the shopper most needs to read it.
 */
const openStack: HTMLElement[] = [];

/** Where the toast region lives when no layer is open. */
let toastHome: ShadowRoot | HTMLElement | undefined;

function rehomeToasts(): void {
  if (!toastRegion) return;
  const target = openStack[openStack.length - 1] ?? toastHome;
  if (!target) return;
  placeToasts();
  if ((toastRegion as unknown as { parentNode?: unknown }).parentNode === target) return;
  try {
    target.appendChild(toastRegion);
  } catch {
    /* a detached target is not worth throwing over */
  }
}

/**
 * Keeps the toasts clear of the top layer's own buttons.
 *
 * A toast sits at the bottom of the screen, which on a phone (and in a short
 * window, or above the keyboard) is exactly where the popup's "Dalje" and
 * "Nazad" are — and above them, since the region is the dialog's top-most
 * child. So while a layer with a footer is on top, the toasts are measured
 * where they would sit, and only if that is on the footer does the region get
 * `is-lifted` and learn how much of the screen's foot the footer takes
 * (`--pz-toast-clear`); the stylesheet then puts them just above it. A popup
 * that ends well above the bottom edge (a tall desktop window, a tablet) keeps
 * its toasts in the dimmed space under it, off the picture and the form.
 * Measured again whenever the layer's viewport or footer changes size and
 * whenever a toast comes or goes.
 */
function placeToasts(): void {
  if (!toastRegion) return;
  const region = toastRegion;
  const layer = openStack[openStack.length - 1];
  let clear = 0;
  const foot = (layer as unknown as { querySelector?: (s: string) => HTMLElement | null } | undefined)
    ?.querySelector?.('.pz-modal-foot');
  const bottom = boxBottom(layer);
  // Nothing on screen, nothing to keep clear: no layout read on every scroll.
  const shown = (region as unknown as { children?: { length: number } }).children?.length ?? 0;
  if (foot && bottom > 0 && shown > 0 && typeof foot.getBoundingClientRect === 'function') {
    try {
      const r = foot.getBoundingClientRect();
      // Where the toasts sit without the lift: their own rules (keyboard,
      // sticky bar, safe area, landscape) decide that, so it is read, not guessed.
      region.classList.remove('is-lifted');
      const own = region.getBoundingClientRect();
      const laidOut = own.bottom > 0;
      const onFoot = laidOut && r.height > 0 && r.top < own.bottom && r.bottom > own.top;
      if (onFoot && r.top > 0 && r.top < bottom) clear = bottom - r.top;
    } catch {
      clear = 0;
    }
  }
  region.classList.toggle('is-lifted', clear > 0);
  setVar(region, '--pz-toast-clear', `${Math.round(clear)}px`);
}

/** Everything else that has to follow the top layer around (the guide). */
const stackListeners = new Set<(top: HTMLElement | undefined) => void>();

/** The layer currently on top, or undefined when the page itself is on top. */
export function topLayer(): HTMLElement | undefined {
  return openStack[openStack.length - 1];
}

/**
 * Tells a listener which layer is on top, now and whenever that changes.
 *
 * The toast region is re-parented for a reason — everything outside an open
 * modal dialog is inert and invisible to the shopper — and anything else that
 * has to stay reachable while a dialog is open needs the same treatment. The
 * shopper guide is the second such thing: the bear must stand inside the wizard
 * (or the map/song dialog) while one is open, or it is a picture of a bear
 * nobody can click. Returns the unsubscribe.
 */
export function onLayerStackChange(
  listener: (top: HTMLElement | undefined) => void,
): () => void {
  stackListeners.add(listener);
  try {
    listener(topLayer());
  } catch {
    /* a listener's own failure must not break the layer that called it */
  }
  return () => {
    stackListeners.delete(listener);
  };
}

/** Called after every push/splice of `openStack`, once the toasts have moved. */
function notifyStack(): void {
  const top = topLayer();
  for (const listener of [...stackListeners]) {
    try {
      listener(top);
    } catch {
      /* one broken listener must not stop the overlay opening or closing */
    }
  }
}

/**
 * A body-level shadow host used when even `position: fixed` is broken for this
 * widget (a transformed ancestor re-roots it) and `<dialog>` is unavailable.
 * It carries a copy of the widget's stylesheet and its custom properties, so
 * the portalled overlay looks identical to the in-place one.
 */
let portalRoot: ShadowRoot | HTMLElement | undefined;

function portalFor(host: ShadowRoot | HTMLElement): ShadowRoot | HTMLElement {
  if (portalRoot) return portalRoot;
  const doc = typeof document === 'undefined' ? undefined : document;
  if (!doc || !doc.body || typeof doc.body.appendChild !== 'function') return host;
  const shell = doc.createElement('div');
  shell.className = 'pz-portal';
  doc.body.appendChild(shell);
  let root: ShadowRoot | HTMLElement = shell;
  if (typeof shell.attachShadow === 'function') {
    try {
      root = shell.attachShadow({ mode: 'open' });
    } catch {
      root = shell;
    }
  }
  // The widget's own <style>, copied so the portal is not naked.
  const source = host as unknown as { querySelectorAll?: (s: string) => ArrayLike<Element> };
  const styles = source.querySelectorAll ? source.querySelectorAll('style') : [];
  for (let i = 0; i < styles.length; i += 1) {
    const copy = doc.createElement('style');
    copy.textContent = (styles[i] as HTMLStyleElement).textContent ?? '';
    root.appendChild(copy);
  }
  // …and the shop-wide custom properties, which live on the widget's host.
  const widgetHost = hostElementOf(host);
  if (widgetHost && widgetHost.getAttribute) {
    const inline = widgetHost.getAttribute('style');
    if (inline) shell.setAttribute('style', inline);
  }
  portalRoot = root;
  return root;
}

/**
 * Where a `position: fixed` element of ours has to live to actually be fixed to
 * the SCREEN.
 *
 * Normally that is the widget's own shadow root. But a page builder's
 * `transform` on any ancestor re-roots fixed positioning to that ancestor, and
 * a toast pinned to the bottom of a 4000px wrapper is a toast nobody ever sees.
 * In that case the element goes into a body-level shadow host instead, which
 * carries a copy of the widget's stylesheet so it still looks like ours.
 */
export function fixedRoot(host: ShadowRoot | HTMLElement): ShadowRoot | HTMLElement {
  const anchor = hostElementOf(host);
  if (!anchor) return host;
  try {
    return fixedIsBroken(anchor) ? portalFor(host) : host;
  } catch {
    return host;
  }
}

/** The element a shadow root belongs to (or the element itself). */
function hostElementOf(node: ShadowRoot | HTMLElement): HTMLElement | null {
  const maybe = node as unknown as { host?: HTMLElement };
  if (maybe.host) return maybe.host;
  return (node as HTMLElement) ?? null;
}

/**
 * Builds a modal layer. Stacked layers (a map picker over the wizard) are
 * ordered by open order in the top layer; z-index no longer matters.
 */
export function createLayer(options: LayerOptions): LayerHandle {
  const {
    host,
    className,
    content,
    label,
    closeOnBackdrop = true,
    closeOnEscape = true,
    onClose,
    onOpen,
    lockScroll: shouldLock = true,
  } = options;
  const dialog = supportsDialog();
  const element = document.createElement(dialog ? 'dialog' : 'div') as HTMLElement;
  element.className = `${className} pz-layer${dialog ? '' : ' pz-layer--fixed'}`;
  element.hidden = true;
  if (!dialog) {
    element.setAttribute('role', 'dialog');
    element.setAttribute('aria-modal', 'true');
    // Focusable, so Escape reaches the layer's own keydown handler.
    element.setAttribute('tabindex', '-1');
  }
  if (label) element.setAttribute('aria-label', label);
  element.appendChild(content);
  // Without <dialog> the overlay is a plain fixed element, which a transformed
  // ancestor would re-root inside the product column; such a widget gets a
  // body-level portal instead.
  const parent = !dialog && fixedIsBroken(hostElementOf(host) ?? element) ? portalFor(host) : host;
  parent.appendChild(element);

  let open = false;
  let unlock: (() => void) | undefined;
  let returnTo: Element | null = null;
  let stopViewport: (() => void) | undefined;
  let escapeHandler: ((event: KeyboardEvent) => void) | undefined;

  const finishClose = (): void => {
    if (!open) return;
    open = false;
    const at = openStack.indexOf(element);
    if (at >= 0) openStack.splice(at, 1);
    rehomeToasts();
    notifyStack();
    if (dialog && (element as HTMLDialogElement).open) {
      try {
        (element as HTMLDialogElement).close();
      } catch {
        /* already closed */
      }
    }
    element.hidden = true;
    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler, true);
      escapeHandler = undefined;
    }
    unlock?.();
    unlock = undefined;
    stopViewport?.();
    stopViewport = undefined;
    const target = returnTo as HTMLElement | null;
    returnTo = null;
    if (target && typeof target.focus === 'function' && target.isConnected !== false) {
      try {
        target.focus({ preventScroll: true });
      } catch {
        /* older engines take no options */
      }
    }
    onClose?.();
  };

  element.addEventListener('pointerdown', (event) => {
    if (closeOnBackdrop && event.target === element) finishClose();
  });
  element.addEventListener('cancel', (event) => {
    // Escape inside a native dialog fires `cancel`; honour or swallow it.
    event.preventDefault();
    if (closeOnEscape) finishClose();
  });
  if (!dialog) {
    element.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Escape' && closeOnEscape) {
        event.preventDefault();
        finishClose();
      }
    });
  }

  return {
    element,
    get isOpen() {
      return open;
    },
    open() {
      if (open) return;
      open = true;
      returnTo = activeElementDeep();
      element.hidden = false;
      if (dialog) {
        // showModal() focuses the dialog's first control itself, with no way to
        // pass preventScroll; if that scrolled the page, it goes back before
        // the lock below records the position to restore on close.
        const scrollX = window.scrollX || 0;
        const scrollY = window.scrollY || 0;
        try {
          (element as HTMLDialogElement).showModal();
        } catch {
          /* already open, or detached; the fixed fallback still shows it */
        }
        if ((window.scrollX || 0) !== scrollX || (window.scrollY || 0) !== scrollY) {
          try {
            window.scrollTo(scrollX, scrollY);
          } catch {
            /* nothing to put back */
          }
        }
      } else {
        try {
          element.focus({ preventScroll: true });
        } catch {
          /* focus is a nicety here, not a requirement */
        }
        // The page keeps its own key handlers without a real dialog, so Escape
        // is caught at the document while this layer is the top one.
        escapeHandler = (event: KeyboardEvent): void => {
          if (event.key !== 'Escape' || !closeOnEscape) return;
          if (openStack[openStack.length - 1] !== element) return;
          event.preventDefault();
          finishClose();
        };
        document.addEventListener('keydown', escapeHandler, true);
      }
      if (shouldLock) unlock = lockScroll();
      // Measured against its own box, and again while the browser's bars
      // settle after the lock; the toasts follow its footer, which grows when
      // it has to say why "Dalje" did nothing.
      const foot =
        typeof element.querySelector === 'function' ? element.querySelector('.pz-modal-foot') : null;
      stopViewport = watchViewport(element, {
        frame: element,
        observe: foot ? [foot] : [],
        onMeasure: () => {
          if (openStack[openStack.length - 1] === element) placeToasts();
        },
      });
      openStack.push(element);
      rehomeToasts();
      notifyStack();
      onOpen?.();
    },
    close: finishClose,
    destroy() {
      finishClose();
      element.remove();
    },
  };
}

/** The focused element, following open shadow roots down. */
export function activeElementDeep(): Element | null {
  let active: Element | null = document.activeElement;
  while (active && active.shadowRoot && active.shadowRoot.activeElement) {
    active = active.shadowRoot.activeElement;
  }
  return active;
}

let lockCount = 0;
let lockRestore: (() => void) | undefined;

/**
 * Locks the page scroll behind an overlay; returns the release function.
 *
 * Reference counted: three stacked overlays lock once and release once, when
 * the last of them closes. On iOS Safari `overflow: hidden` on the root does
 * not stop the body scrolling, so the body is pinned with `position: fixed`
 * at its current scroll offset and scrolled back on release. On desktop the
 * vanished scrollbar is compensated so the page does not shift sideways.
 */
export function lockScroll(): () => void {
  lockCount += 1;
  if (lockCount === 1) {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const gutter = Math.max(0, (window.innerWidth || 0) - (html.clientWidth || 0));
    const ios = isIOS();
    const previous = {
      htmlOverflow: html.style.overflow,
      htmlPadding: html.style.paddingRight,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
    };
    html.style.overflow = 'hidden';
    // The scrollbar's width, given back as padding, so the page does not jump
    // sideways the moment an overlay opens on a desktop browser.
    if (gutter > 0) html.style.paddingRight = `${gutter}px`;
    if (ios) {
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.left = `-${scrollX}px`;
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    }
    lockRestore = () => {
      html.style.overflow = previous.htmlOverflow;
      html.style.paddingRight = previous.htmlPadding;
      if (ios) {
        body.style.position = previous.bodyPosition;
        body.style.top = previous.bodyTop;
        body.style.left = previous.bodyLeft;
        body.style.width = previous.bodyWidth;
        body.style.overflow = previous.bodyOverflow;
        window.scrollTo(scrollX, scrollY);
      }
    };
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      lockRestore?.();
      lockRestore = undefined;
    }
  };
}

/** iOS (including iPadOS, which reports as a Mac with touch). */
export function isIOS(): boolean {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const ua = nav?.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav?.maxTouchPoints ?? 0) > 1);
}

/**
 * True inside Instagram, Facebook, TikTok, Messenger, LINE, Snapchat and
 * similar in-app browsers, whose own toolbars overlay the bottom of the page
 * and are invisible to `env(safe-area-inset-bottom)`.
 */
export function isInAppBrowser(): boolean {
  const ua = (typeof navigator === 'undefined' ? '' : navigator.userAgent) || '';
  return /FBAN|FBAV|FB_IAB|Instagram|TikTok|musical_ly|Line\/|Snapchat|Twitter|Pinterest\/|GSA\//i.test(
    ua,
  );
}

/** Touch-first device: hover affordances are unusable, targets must be thumb-sized. */
export function pointerIsCoarse(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches;
}

/** A real hover exists (mouse/trackpad) — hover zoom and hover tools are useful. */
export function canHover(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
  );
}

/** Below this, what the visible area leaves out of the screen is not a keyboard. */
const KEYBOARD_MIN = 80;

/** What the visible part of the screen is, measured against a layer's own box. */
export interface ViewportMetrics {
  /** The visible height and width, in CSS px. */
  height: number;
  width: number;
  /** The visible area's offset from the top of the layout viewport. */
  top: number;
  /**
   * How much of the frame lies below the visible area: the on-screen keyboard,
   * or the part of a zoomed or over-wide page that is out of view.
   */
  inset: number;
  /** True only when that inset really is a keyboard: big, and a field has focus. */
  keyboard: boolean;
}

/**
 * The visual viewport, as the layers read it.
 *
 * The keyboard is what the visible area leaves out below it — measured against
 * the bottom edge of the layer's own fixed box (`frameBottom`), which is where
 * the sheet's footer sits, rather than `innerHeight`, which can lag behind the
 * box while a browser bar moves. And the arithmetic alone is not a keyboard: on
 * a page wider than the screen (a theme's overflowing row) or a pinch-zoomed
 * one, the layout viewport is far taller than what is visible, and the gap came
 * out at 2000px with no keyboard anywhere — a false keyboard that hid the
 * popup's head. So a keyboard also needs a field to type into.
 */
export function viewportMetrics(input: {
  vv?: { height: number; width: number; offsetTop: number } | null;
  innerHeight: number;
  innerWidth: number;
  /** The bottom edge of the layer's fixed box, in layout px; 0 when it has none. */
  frameBottom?: number;
  /** An editable field (text input, textarea, select, contenteditable) has focus. */
  editing: boolean;
}): ViewportMetrics {
  const height = input.vv?.height ?? input.innerHeight;
  const width = input.vv?.width ?? input.innerWidth;
  const top = input.vv?.offsetTop ?? 0;
  const bottom = input.frameBottom && input.frameBottom > 0 ? input.frameBottom : input.innerHeight;
  const inset = Math.max(0, bottom - height - top);
  return { height, width, top, inset, keyboard: input.editing && inset > KEYBOARD_MIN };
}

/** Input types that never bring up an on-screen keyboard. */
const NO_KEYBOARD_INPUT = /^(button|checkbox|color|file|hidden|image|radio|range|reset|submit)$/i;

/**
 * True while the focused element is one the phone types into. An iframe does
 * not count: the widget's own (the Spotify and YouTube players) take taps, not
 * typing, and one focused on a zoomed page brought the false keyboard back.
 */
export function isEditingField(el: Element | null): boolean {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NO_KEYBOARD_INPUT.test((el as HTMLInputElement).type || 'text');
  return (el as HTMLElement).isContentEditable === true;
}

/** Writes a custom property only when it changed, so a re-measure costs no style work. */
function setVar(el: HTMLElement, name: string, value: string): void {
  const style = el.style;
  if (typeof style.getPropertyValue === 'function' && style.getPropertyValue(name) === value) return;
  style.setProperty(name, value);
}

/** The bottom edge of a fixed box, or 0 when it is not laid out (or not measurable). */
function boxBottom(el: HTMLElement | undefined): number {
  if (!el || typeof el.getBoundingClientRect !== 'function') return 0;
  try {
    const rect = el.getBoundingClientRect();
    return rect.height > 0 ? rect.bottom : 0;
  } catch {
    return 0;
  }
}

export interface ViewportWatchOptions {
  /**
   * The fixed, full-screen box the target's sheet lives in — a layer. Its
   * bottom edge is what the keyboard is measured against, any change to its
   * size is a reason to measure again, and it is re-measured for a moment
   * after it opens, when the browser's own bars are still settling.
   */
  frame?: HTMLElement;
  /** Other boxes whose change of size is a reason to measure again (a footer). */
  observe?: Element[];
  /** Called after every measure, for whatever else sizes itself from the screen. */
  onMeasure?: () => void;
}

/**
 * Mirrors the visual viewport into custom properties on `target`:
 *   --pz-vvh   the visible height in px (keyboard and URL bar excluded)
 *   --pz-vvw   the visible width in px
 *   --pz-vv-top the visual viewport's offset from the layout viewport
 *   --pz-kb    how much of the screen's foot the visible area leaves out: the
 *              on-screen keyboard (0 when closed), or a zoomed page's hidden part
 * and the `has-keyboard` class, only while a field is being typed into.
 * Returns a stop function. Falls back to `innerHeight` where the
 * VisualViewport API is missing.
 *
 * Events alone are not enough to trust. A phone's browser can bring its bars
 * back after the popup locked the page (the footer then sat under the bottom
 * edge: "the buttons appear for a second and then disappear"), iOS delivers
 * `resize` before `visualViewport` has its new size, and a hidden tab gets
 * nothing at all. So besides the events, the frame's own size is observed, the
 * page coming back (pageshow, visibility) and focus moving trigger a measure,
 * and a layer that just opened is measured again over its first two seconds.
 * The stylesheet caps the sheet at the frame's height as well, so a stale
 * number can shorten it but never push its footer off the screen.
 */
export function watchViewport(target: HTMLElement, options: ViewportWatchOptions = {}): () => void {
  const { frame, observe = [], onMeasure } = options;
  const vv = typeof window === 'undefined' ? undefined : window.visualViewport;
  const apply = (): void => {
    const metrics = viewportMetrics({
      vv,
      innerHeight: window.innerHeight,
      innerWidth: window.innerWidth,
      frameBottom: boxBottom(frame),
      editing: isEditingField(activeElementDeep()),
    });
    setVar(target, '--pz-vvh', `${Math.round(metrics.height)}px`);
    setVar(target, '--pz-vvw', `${Math.round(metrics.width)}px`);
    setVar(target, '--pz-vv-top', `${Math.round(metrics.top)}px`);
    setVar(target, '--pz-kb', `${Math.round(metrics.inset)}px`);
    // A forced toggle that matches changes nothing, so no style work either.
    target.classList.toggle('has-keyboard', metrics.keyboard);
    try {
      onMeasure?.();
    } catch {
      /* a follower's failure must not stop the measuring */
    }
  };

  // One measure per frame for the bursty sources (focus, observers); a timer
  // backs the frame up, because a hidden tab never runs one.
  let frameId: number | undefined;
  let soonTimer: number | undefined;
  const soon = (): void => {
    if (frameId !== undefined || soonTimer !== undefined) return;
    const run = (): void => {
      if (frameId !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameId);
      window.clearTimeout(soonTimer);
      frameId = undefined;
      soonTimer = undefined;
      apply();
    };
    if (typeof requestAnimationFrame === 'function') frameId = requestAnimationFrame(run);
    soonTimer = window.setTimeout(run, 100);
  };

  apply();
  const passive = { passive: true } as AddEventListenerOptions;
  vv?.addEventListener('resize', apply);
  vv?.addEventListener('scroll', apply);
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('scroll', soon, passive);
  window.addEventListener('pageshow', soon);
  const doc = typeof document === 'undefined' ? undefined : document;
  // A field gaining or losing focus is what turns the keyboard on or off.
  doc?.addEventListener('focusin', soon);
  doc?.addEventListener('focusout', soon);
  doc?.addEventListener('visibilitychange', soon);

  let observer: ResizeObserver | undefined;
  const boxes = frame ? [frame, ...observe] : observe;
  if (boxes.length > 0 && typeof ResizeObserver === 'function') {
    try {
      observer = new ResizeObserver(soon);
      for (const box of boxes) observer.observe(box);
    } catch {
      observer?.disconnect();
      observer = undefined;
    }
  }
  const settle: number[] = [];
  if (frame) {
    // The browser's bars settle after the page is locked, not before.
    soon();
    for (const ms of [250, 600, 1200, 2000]) settle.push(window.setTimeout(apply, ms));
    // A sheet that slides in (on open, or when turning the phone swaps its
    // layout) is measured where it lands, not half way up the screen.
    frame.addEventListener('animationend', soon);
  }

  return () => {
    vv?.removeEventListener('resize', apply);
    vv?.removeEventListener('scroll', apply);
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    window.removeEventListener('scroll', soon, passive);
    window.removeEventListener('pageshow', soon);
    doc?.removeEventListener('focusin', soon);
    doc?.removeEventListener('focusout', soon);
    doc?.removeEventListener('visibilitychange', soon);
    observer?.disconnect();
    frame?.removeEventListener('animationend', soon);
    for (const timer of settle) window.clearTimeout(timer);
    if (frameId !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frameId);
    window.clearTimeout(soonTimer);
    frameId = undefined;
    soonTimer = undefined;
  };
}

/** Elements pinned at a probe point, or an empty list where the API is missing. */
function probe(x: number, y: number): Element[] {
  if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') {
    return [];
  }
  try {
    return document.elementsFromPoint(x, y) ?? [];
  } catch {
    return [];
  }
}

/**
 * Height of whatever the theme pins to the top of the viewport (a fixed or
 * sticky header), so a sticky preview can sit under it instead of behind it.
 */
export function stickyTopOffset(): number {
  let offset = 0;
  const seen = new Set<Element>();
  const probeX = Math.max(1, Math.floor(window.innerWidth / 2));
  for (const y of [1, 40, 80, 120]) {
    for (const el of probe(probeX, y)) {
      if (seen.has(el) || !(el instanceof HTMLElement)) continue;
      seen.add(el);
      const style = getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      const rect = el.getBoundingClientRect();
      if (rect.top > 2 || rect.height >= window.innerHeight * 0.5 || rect.height === 0) continue;
      offset = Math.max(offset, rect.bottom);
    }
  }
  return Math.round(offset);
}

/**
 * Height of whatever the theme pins to the BOTTOM of the viewport — the sticky
 * add-to-cart bars Flatsome, Woodmart, Astra and Botiga ship — so a toast can
 * sit above it instead of underneath it, where nobody would ever read it.
 */
export function bottomBarOffset(): number {
  if (typeof window === 'undefined') return 0;
  let offset = 0;
  const seen = new Set<Element>();
  const probeX = Math.max(1, Math.floor(window.innerWidth / 2));
  const bottom = window.innerHeight;
  for (const up of [1, 24, 48]) {
    for (const el of probe(probeX, Math.max(0, bottom - up))) {
      if (seen.has(el) || !(el instanceof HTMLElement)) continue;
      seen.add(el);
      const style = getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      const rect = el.getBoundingClientRect();
      if (rect.height === 0 || rect.height >= bottom * 0.5) continue;
      if (rect.bottom < bottom - 2) continue;
      offset = Math.max(offset, bottom - rect.top);
    }
  }
  return Math.round(offset);
}

/** The next element up, crossing out of an open shadow root when it has to. */
function climb(node: Element): Element | null {
  if (node.parentElement) return node.parentElement;
  const root = typeof node.getRootNode === 'function' ? node.getRootNode() : null;
  const host = (root as ShadowRoot | null)?.host;
  return (host as Element | undefined) ?? null;
}

/**
 * True when an ancestor of `el` would break `position: sticky` (any overflow
 * other than visible) — the case in most WooCommerce themes' summary column.
 */
export function stickyIsBroken(el: Element): boolean {
  let node: Element | null = climb(el);
  while (node && node !== document.body && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (
      /(hidden|auto|scroll|clip)/.test(
        `${style.overflow} ${style.overflowX} ${style.overflowY}`,
      )
    ) {
      return true;
    }
    node = climb(node);
  }
  return false;
}

/**
 * True when an ancestor of `el` re-roots `position: fixed` (transform,
 * filter, perspective, contain, will-change) — page-builder themes do this.
 */
export function fixedIsBroken(el: Element): boolean {
  let node: Element | null = climb(el);
  while (node && node !== document.documentElement) {
    const style = getComputedStyle(node);
    if (
      (style.transform && style.transform !== 'none') ||
      (style.filter && style.filter !== 'none') ||
      (style.perspective && style.perspective !== 'none') ||
      /(layout|paint|strict|content)/.test(style.contain || '') ||
      /(transform|filter|perspective)/.test(style.willChange || '')
    ) {
      return true;
    }
    node = climb(node);
  }
  return false;
}

/**
 * Calls back when `el` changes size. ResizeObserver where it exists, else the
 * window resize event; either way debounced so a rubber-banding layout does
 * not trigger a render per frame.
 */
export function observeSize(el: Element, callback: () => void, debounceMs = 120): () => void {
  let timer: number | undefined;
  const fire = (): void => {
    window.clearTimeout(timer);
    timer = window.setTimeout(callback, debounceMs);
  };
  if (typeof ResizeObserver === 'function') {
    const observer = new ResizeObserver(fire);
    observer.observe(el);
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }
  window.addEventListener('resize', fire);
  return () => {
    window.removeEventListener('resize', fire);
    window.clearTimeout(timer);
  };
}

/**
 * Brings a just-focused field into view above the on-screen keyboard, inside
 * whichever scroll container holds it. Waits for the keyboard animation.
 */
export function revealFocused(input: HTMLElement): void {
  window.setTimeout(() => {
    try {
      input.scrollIntoView({ block: 'center' });
    } catch {
      input.scrollIntoView();
    }
  }, 300);
}

export interface ToastOptions {
  message: string;
  kind?: ToastKind;
  /** Auto-dismiss after this long; 0 keeps it until dismissed (default 5000, errors 8000). */
  timeoutMs?: number;
  action?: { label: string; onClick: () => void };
}

export interface ToastHandle {
  dismiss(): void;
}

let toastRegion: HTMLElement | undefined;

/**
 * A viewport-anchored notification that is always on screen: bottom-centre,
 * above the keyboard, the safe area and in-app toolbars; announced to screen
 * readers; stacks; never covers the wizard's own buttons.
 */
export function showToast(host: ShadowRoot | HTMLElement, options: ToastOptions): ToastHandle {
  toastHome = fixedRoot(host);
  if (!toastRegion || (toastRegion as unknown as { isConnected?: boolean }).isConnected === false) {
    toastRegion = document.createElement('div');
    toastRegion.className = 'pz-toasts';
    toastRegion.setAttribute('aria-live', 'polite');
    toastRegion.setAttribute('role', 'status');
    if (isInAppBrowser()) {
      toastRegion.classList.add('is-inapp');
      toastRegion.style.setProperty('--pz-inapp-bottom', '44px');
    }
    // Placed again after its own keyboard number moves, whichever of it and
    // the popup hears the event last.
    watchViewport(toastRegion, { onMeasure: placeToasts });
  }
  // A theme's sticky add-to-cart bar owns the bottom of the screen; measured
  // now rather than at boot, because such bars appear on scroll.
  toastRegion.style.setProperty('--pz-bottom-bar', `${bottomBarOffset()}px`);
  rehomeToasts();

  const kind = options.kind ?? 'info';
  const toast = document.createElement('div');
  toast.className = `pz-toast is-${kind}`;
  const text = document.createElement('span');
  text.className = 'pz-toast-text';
  text.textContent = options.message;
  toast.appendChild(text);
  if (options.action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pz-toast-action';
    button.textContent = options.action.label;
    button.addEventListener('click', () => {
      options.action?.onClick();
      dismiss();
    });
    toast.appendChild(button);
  }
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'pz-toast-close';
  close.setAttribute('aria-label', 'Zatvori');
  close.innerHTML = '&#10005;';
  close.addEventListener('click', () => dismiss());
  toast.appendChild(close);
  toastRegion.appendChild(toast);
  // With its own height now: whether it would land on the popup's buttons.
  placeToasts();

  let timer: number | undefined;
  let dismissed = false;
  const dismiss = (): void => {
    if (dismissed) return;
    dismissed = true;
    window.clearTimeout(timer);
    toast.classList.add('is-leaving');
    window.setTimeout(() => {
      toast.remove();
      placeToasts();
    }, 180);
  };
  const timeout = options.timeoutMs ?? (kind === 'error' ? 8000 : 5000);
  if (timeout > 0) timer = window.setTimeout(() => dismiss(), timeout);
  return { dismiss };
}

/** Test seam: forgets the module-level toast region and open-layer stack. */
export function resetLayersForTest(): void {
  toastRegion = undefined;
  toastHome = undefined;
  portalRoot = undefined;
  openStack.splice(0, openStack.length);
  stackListeners.clear();
  lockCount = 0;
  lockRestore = undefined;
}
